import { Box, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DoneIcon from '@material-ui/icons/Done'
import { useState } from 'react'
import { Identifier, useNotify } from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Grid as BaseGrid } from 'shared/components/Base/Grid'
import { Button } from 'shared/components/Button/Button'
import { OnboardingTaskList } from 'shared/components/Onboarding/OnboardingTaskList'
import { parseOnboardingTaskList as _parseOnboardingTaskList } from 'shared/components/Onboarding/OnboardingTasklist.util'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { isProductionEnv } from 'shared/config/config'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { useOnboardingChecklist } from 'shared/hooks/query/useOnboardingChecklist'
import { useUserV3 } from 'shared/hooks/query/useUserV3'
import { resetEsignLeaseAgreement } from 'shared/models/esign'
import { OnboardingChecklist, OnboardingSubChecklist } from 'shared/models/onboarding'
import { SlimTeam, Team } from 'shared/models/team'
import { User } from 'shared/models/user'
import { ProfilePageTab } from 'shared/pages/ProfilePage/ProfilePage'
import { errorD1 } from 'shared/theme/colors'
import { styled } from 'styled-components'

import {
  completeChecklistItem,
  confirmFleetApplication,
  confirmMedicalCardForUser,
  confirmOccupationalAccidentInsurance,
  confirmSignLeaseAgreementForTeam,
  confirmTrailer,
  confirmTruck,
  fileUccStatus,
} from '../../api/core'
import { OnboardingChecklistName, TaskStatus } from '../../helpers/constants'
import { BaseActionDetailButton } from '../Common/ActionButtons'
import { AppOnboardingFlow } from './UserCtonCtProfile'
import { UserEmploymentConfirm } from './UserEmploymentConfirm'
import { UserOnboardingSidebar } from './UserOnboardingSidebar'
import { UserOnboardingSignLease } from './UserOnboardingSignLease'

const useStyles = makeStyles({
  subTasks: {
    marginLeft: '40px',
  },
  title: {
    width: '300px',
    marginRight: '20px',
  },
  subTitle: {
    width: '260px',
    marginRight: '20px',
  },
  statusText: {
    width: '200px',
    marginRight: '20px',
  },
  cta: {
    margin: '0 !important',
  },
})

interface UserOnboardingProps {
  user: User
  team?: Team
  permissions: any
  setActiveTab: HandlerOf<ProfilePageTab>
  setAppOnboardingFlow: HandlerOf<AppOnboardingFlow>
}

export const UserOnboarding = ({
  user,
  team,
  permissions,
  setActiveTab,
  setAppOnboardingFlow,
}: UserOnboardingProps) => {
  const userId = user?.id
  const { refetch } = useOnboardingChecklist(userId, { enabled: false })

  if (!user || !team || !permissions) {
    return null
  }

  function parseOnboardingTaskList(
    onboardingChecklist: ReadonlyArray<OnboardingChecklist>,
    user: User,
    team: SlimTeam
  ) {
    return _parseOnboardingTaskList(
      onboardingChecklist,
      user,
      team,
      undefined,
      undefined,
      (task) => (
        <>
          <TaskCta
            task={task}
            setActiveTab={setActiveTab}
            user={user}
            refetch={refetch}
            setAppOnboardingFlow={setAppOnboardingFlow}
          />
          <TaskCompleteCta user={user} task={task} refetch={refetch} />
        </>
      ),
      (task) => getStatusDebug(task, user)
    )
  }

  return (
    <BaseGrid columns="1fr 200px" gap="70px">
      <div>
        <OnboardingTaskList user={user} team={team} parseTaskList={parseOnboardingTaskList} />
      </div>
      <div>
        <Box mt={-1.2} />
        <UserOnboardingSidebar user={user} permissions={permissions} />
      </div>
    </BaseGrid>
  )
}

interface TaskCtaProps {
  task: OnboardingChecklist | OnboardingSubChecklist
  refetch: () => Promise<any>
  user: User
  setActiveTab: HandlerOf<ProfilePageTab>
  setAppOnboardingFlow: HandlerOf<AppOnboardingFlow>
}

function TaskCta({ task, user, setActiveTab, refetch, setAppOnboardingFlow }: TaskCtaProps) {
  const classes = useStyles()
  const confirmOrConfirmedLabel = task.status === TaskStatus.COMPLETED ? 'Confirmed' : 'Confirm'

  if (task.name === OnboardingChecklistName.DRIVER_APPLICATION) {
    return (
      <FlexBox gap="20px">
        <BaseActionDetailButton
          record={{ id: user.id }}
          actionRequest={confirmMedicalCardForUser}
          actionTitle={confirmOrConfirmedLabel}
          actionText="Confirm medical card?"
          className={classes.cta}
          disabled={task.status !== TaskStatus.IN_PROGRESS}
          handleRequestEnd={refetch}
          useNewButton
        />
        {user?.featureFlags?.publicConsoleOnboarding &&
        (task.status === TaskStatus.NOT_STARTED ||
          (task.status === TaskStatus.ERROR && task.statusDetail !== 'MISSING_BG_CHECK')) ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => setAppOnboardingFlow(AppOnboardingFlow.DRIVER_APPLICATION)}
          >
            Start
          </Button>
        ) : null}
      </FlexBox>
    )
  }

  if (task.name === OnboardingChecklistName.EMPLOYMENT_HISTORY) {
    return (
      <FlexBox gap="20px">
        <UserEmploymentConfirm
          record={{ id: user.id }}
          className={classes.cta}
          setActiveTab={setActiveTab}
          disabled={task.status !== TaskStatus.IN_PROGRESS}
          onSuccess={refetch}
        />

        {user?.featureFlags?.publicConsoleOnboarding &&
        (task.status === TaskStatus.NOT_STARTED ||
          (task.status === TaskStatus.ERROR && task.statusDetail !== 'NOT_SUBMITTED')) ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => setAppOnboardingFlow(AppOnboardingFlow.EMPLOYMENT_HISTORY)}
          >
            Start
          </Button>
        ) : null}
      </FlexBox>
    )
  }

  if (task.name === OnboardingChecklistName.SIGN_AGREEMENT) {
    if (task.status === TaskStatus.COMPLETED) {
      return (
        <BaseActionDetailButton
          record={{ id: user.team! }}
          actionRequest={async () => {
            resetEsignLeaseAgreement(user.team)
          }}
          actionTitle="Reset the lease agreement?"
          actionText=" After reset you will be able to generate new lease agreement."
          className={classes.cta}
          handleRequestEnd={refetch}
          useNewButton
        />
      )
    }
    if (user.featureFlags?.publicInAppSignLeaseAgreement) {
      return (
        <FlexBox alignItems="center" gap="20px">
          <UserOnboardingSignLease user={user} task={task} refetchChecklist={refetch} />
          {/*
            This button needs to be visible only so the users that went thorugh old flow could still be confirmed.
            After all users go through the new flow we can remove it.
          */}
          {task.status === TaskStatus.PREREQUISITES_NOT_MET &&
          task.statusDetail !== 'MISSING_INFORMATION_FOR_LEASE_AGREEMENT' ? (
            <BaseActionDetailButton
              record={{ id: user.team! }}
              actionRequest={confirmSignLeaseAgreementForTeam}
              actionTitle="Confirm"
              actionText={[
                <br />,
                'Use this tool only if you have used deprecated SalesForce flow to sent the lease agreement.',
              ]}
              className={classes.cta}
              handleRequestEnd={refetch}
              useNewButton
            />
          ) : null}
        </FlexBox>
      )
    }

    if (task.status === TaskStatus.COMPLETED) return null

    if (task.name === OnboardingChecklistName.FLEX_AGREEMENT) {
      return (
        <Grid container spacing={2}>
          <Grid item>
            <BaseActionDetailButton
              record={{ id: user.team! }}
              actionRequest={confirmSignLeaseAgreementForTeam}
              actionTitle="Mark in review"
              actionText="Confirm sign lease agreement?"
              className={classes.cta}
              disabled={task.status !== TaskStatus.NOT_STARTED}
              handleRequestEnd={refetch}
              useNewButton
            />
          </Grid>
          <Grid item>
            <BaseActionDetailButton
              record={{ id: user.team! }}
              actionRequest={fileUccStatus}
              actionTitle={confirmOrConfirmedLabel}
              actionText="Confirm fill the ucc status?"
              className={classes.cta}
              disabled={task.status !== TaskStatus.IN_PROGRESS}
              handleRequestEnd={refetch}
              useNewButton
            />
          </Grid>
        </Grid>
      )
    }

    if (
      task.name === OnboardingChecklistName.FLEET_APPLICATION ||
      task.name === OnboardingChecklistName.FLEET_DETAILS
    ) {
      return (
        <BaseActionDetailButton
          record={{ id: user.team! }}
          actionRequest={confirmFleetApplication}
          actionTitle={confirmOrConfirmedLabel}
          actionText="Confirm fleet application?"
          className={classes.cta}
          disabled={task.status !== TaskStatus.IN_PROGRESS}
          handleRequestEnd={refetch}
          useNewButton
        />
      )
    }

    return (
      <BaseActionDetailButton
        record={{ id: user.team! }}
        actionRequest={confirmSignLeaseAgreementForTeam}
        actionTitle={confirmOrConfirmedLabel}
        actionText="Confirm sign lease agreement?"
        className={classes.cta}
        disabled={task.status === TaskStatus.PREREQUISITES_NOT_MET}
        handleRequestEnd={refetch}
        useNewButton
      />
    )
  }

  if (task.name === OnboardingChecklistName.INSURANCE_INFORMATION) {
    return (
      <BaseActionDetailButton
        record={{ id: user.id }}
        actionRequest={confirmOccupationalAccidentInsurance}
        actionTitle={confirmOrConfirmedLabel}
        actionText="Confirm occupational accident insurance?"
        className={classes.cta}
        disabled={task.status !== TaskStatus.IN_PROGRESS}
        handleRequestEnd={refetch}
        useNewButton
      />
    )
  }

  if (task.name === OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT_DOCUMENTS) {
    const isTruck = Boolean(task.context?.truck)
    return (
      <BaseActionDetailButton
        record={{ id: (isTruck ? task.context?.truck?.id : task.context?.trailer?.id) as Identifier }}
        actionRequest={isTruck ? confirmTruck : confirmTrailer}
        actionTitle={confirmOrConfirmedLabel}
        actionText={isTruck ? 'Confirm truck?' : 'Confirm Trailer?'}
        className={classes.cta}
        disabled={task.status !== TaskStatus.IN_PROGRESS}
        handleRequestEnd={refetch}
        useNewButton
      />
    )
  }

  return null
}

function getStatusDebug(
  task: OnboardingChecklist | OnboardingSubChecklist,
  user: User
): React.ReactNode | React.ReactNode[] {
  switch (task.status) {
    case TaskStatus.ERROR: {
      if (task.name === OnboardingChecklistName.DRIVER_APPLICATION) {
        if (task.statusDetail === 'MISSING_BG_CHECK') return null

        const missingFields: any = []
        if (!user.driver.isDriverApplicationFormComplete) missingFields.push('Driver Information')
        if (!user.driver.isLicenseInfoComplete) missingFields.push('License info')
        if (!user.driver.isEmergencyContactComplete) missingFields.push('Emergency contact')
        if (!user.driver.isMedicalCardValid) missingFields.push('Medical card')

        if (missingFields.length) {
          return (
            <>
              {': '}
              <Text variant="paragraph3" color={errorD1} as="span" ml={0.5}>
                {missingFields.join(', ')}
              </Text>
            </>
          )
        }
        return null
      }
      return null
    }

    default:
      return null
  }
}

function TaskCompleteCta({
  task,
  user,
  refetch,
}: Omit<TaskCtaProps, 'setActiveTab' | 'setAppOnboardingFlow'>) {
  const notify = useNotify()
  const [isLoading, setIsLoading] = useState(false)
  const { refetch: refetchUser } = useUserV3(user.id, { enabled: false })

  if (task.status === TaskStatus.COMPLETED || isProductionEnv()) return null

  async function handleComplete() {
    setIsLoading(true)
    try {
      await completeChecklistItem({ userId: user.id, tasklistName: task.name })
      await refetch()
      await refetchUser()
    } catch (err) {
      notify(JSON.stringify(err))
    }
    setIsLoading(false)
  }

  return (
    <CompleteCta>
      {isLoading ? <InlineSpinner size="0.8em" /> : <StyledCheckCircleOutlineIcon onClick={handleComplete} />}
    </CompleteCta>
  )
}

const CompleteCta = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
  display: none;

  .tasklist-card:hover & {
    display: block;
  }
`

const StyledCheckCircleOutlineIcon = styled(DoneIcon)`
  font-size: 16px;
  color: royalblue;
`
